import * as React from "react";
import { Loader2, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { WorkspaceNode } from "../../../types/workspace";
import { WORKSPACE_NODE_TYPE_LABELS } from "./workspaceGraphUtils";
import { describeWorkspaceEntityDelete } from "./workspaceNodeDeleteUtils";

export type WorkspaceRemoveNodeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: WorkspaceNode | null;
  nodeTitle: string;
  /** Extra note when connected descendants will also leave the canvas. */
  downstreamNote?: string | null;
  /** When false, hide the permanent delete action. */
  canDeleteEntity?: boolean;
  busy?: boolean;
  onConfirmRemove: () => void | Promise<void>;
  onConfirmDelete: () => void | Promise<void>;
};

/**
 * Confirms canvas unlink and (when allowed) permanent entity delete —
 * same dialog layout as before, with Delete permanently next to Remove.
 */
export function WorkspaceRemoveNodeDialog({
  open,
  onOpenChange,
  node,
  nodeTitle,
  downstreamNote = null,
  canDeleteEntity = false,
  busy = false,
  onConfirmRemove,
  onConfirmDelete,
}: WorkspaceRemoveNodeDialogProps): React.ReactElement {
  const typeLabel = node ? WORKSPACE_NODE_TYPE_LABELS[node.type] : "Item";
  const title = nodeTitle.trim() || typeLabel;
  const deleteCopy = node
    ? describeWorkspaceEntityDelete(node, nodeTitle)
    : { confirmLabel: "Delete permanently" };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        showCloseButton={false}
        data-workspace-remove-node-dialog=""
        className="gap-4 p-0 sm:max-w-md"
      >
        <DialogClose asChild>
          <button
            type="button"
            data-workspace-remove-node-dismiss=""
            aria-label="Close"
            disabled={busy}
            className="absolute top-4 right-4 z-10 inline-flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-white/5 dark:hover:text-white"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>

        <DialogHeader className="gap-2 px-6 pt-5 pb-0 pr-12 text-left">
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            Remove from workspace?
          </DialogTitle>
          <DialogDescription className="font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white">
            Your{" "}
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">{title}</span>{" "}
            {typeLabel.toLowerCase()} leaves this canvas only. You can still find it in the Workspace
            hub.
            {canDeleteEntity ? (
              <span className="block pt-2 text-gray-500 dark:text-white/80">
                Or delete it permanently. That cannot be undone.
              </span>
            ) : null}
            {downstreamNote ? (
              <span className="block pt-2 text-gray-500 dark:text-white/80">{downstreamNote}</span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter
          showCloseButton={false}
          className="mx-0 mb-0 mt-0 w-full flex-row flex-wrap justify-end gap-2 rounded-b-[6px] border-t border-border bg-muted/50 px-6 py-4"
        >
          {canDeleteEntity ? (
            <Button
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              className="h-8 shrink-0 px-4"
              disabled={busy || !node}
              onClick={() => void onConfirmDelete()}
            >
              {busy ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : null}
              <span className="normal-case tracking-normal">{deleteCopy.confirmLabel}</span>
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline-red"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-4"
            disabled={busy || !node}
            onClick={() => void onConfirmRemove()}
          >
            {busy ? (
              <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
            ) : null}
            <span className="normal-case tracking-normal">Remove</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
